import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, FileText, LogOut, ArrowLeft, User, FolderOpen, Lock, Images, X, ChevronLeft, ChevronRight, Archive, ClipboardList, Calendar, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TestimonialModal from '../components/TestimonialModal';
import SessionDataModal from '../components/SessionDataModal';
import { ZipPartsDialog, triggerDownload } from '../components/ZipPartsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export const PrivateArea = () => {
  const [client, setClient] = useState(null);
  const [privateFiles, setPrivateFiles] = useState([]);
  const [sharedDocs, setSharedDocs] = useState([]);
  const [sessionDataList, setSessionDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('private');
  const [activeDocCategory, setActiveDocCategory] = useState('all');
  const [downloadingZip, setDownloadingZip] = useState(null);
  const [hasTestimonial, setHasTestimonial] = useState(false);
  
  // Modal states
  const [testimonialModalOpen, setTestimonialModalOpen] = useState(false);
  const [sessionDataModalOpen, setSessionDataModalOpen] = useState(false);
  const [editingSessionData, setEditingSessionData] = useState(null);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [zipParts, setZipParts] = useState(null);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxTitle, setLightboxTitle] = useState('');
  
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('client_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % lightboxImages.length);
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImages.length]);

  const checkAuth = async () => {
    const storedClient = sessionStorage.getItem('client');
    try {
      if (storedClient) {
        setClient(JSON.parse(storedClient));
      }

      const response = await fetch(`${API}/client/me`, { headers: getAuthHeaders() });

      if (response.ok) {
        const data = await response.json();
        setClient(data);
        sessionStorage.setItem('client', JSON.stringify(data));
        fetchData();
      } else {
        // Fallback: if we have client data and token in sessionStorage, trust it
        const token = sessionStorage.getItem('client_token');
        if (storedClient && token) {
          setClient(JSON.parse(storedClient));
          fetchData();
        } else {
          sessionStorage.removeItem('client');
          sessionStorage.removeItem('client_token');
          navigate('/client/login');
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // Network/CORS error - fallback to sessionStorage
      const token = sessionStorage.getItem('client_token');
      if (storedClient && token) {
        setClient(JSON.parse(storedClient));
        fetchData();
      } else {
        sessionStorage.removeItem('client');
        sessionStorage.removeItem('client_token');
        navigate('/client/login');
      }
    }
  };

  const fetchData = async () => {
    try {
      const token = sessionStorage.getItem('client_token');
      const authHeaders = getAuthHeaders();
      
      const [filesRes, docsRes, testimonialRes, sessionDataRes] = await Promise.all([
        fetch(`${API}/client/files`, { headers: authHeaders }),
        fetch(`${API}/client/shared-documents`, { headers: authHeaders }),
        fetch(`${API}/client/has-testimonial`, { headers: authHeaders }),
        fetch(`${API}/client/session-data`, { headers: authHeaders })
      ]);
      
      if (filesRes.ok) setPrivateFiles(await filesRes.json());
      if (docsRes.ok) setSharedDocs(await docsRes.json());
      if (testimonialRes.ok) {
        const data = await testimonialRes.json();
        setHasTestimonial(data.has_testimonial);
      }
      if (sessionDataRes.ok) {
        const data = await sessionDataRes.json();
        setSessionDataList(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/client/logout`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.removeItem('client');
    sessionStorage.removeItem('client_token');
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const openGallery = (file) => {
    if (file.images && file.images.length > 0) {
      setLightboxImages(file.images);
      setLightboxTitle(file.title);
      setLightboxIndex(0);
      setLightboxOpen(true);
    }
  };

  const downloadZip = async (file) => {
    setDownloadingZip(file.file_id);
    const sizeHint = file.total_size_bytes > 0 ? ` (≈ ${formatBytes(file.total_size_bytes)})` : '';
    toast.info(`Preparando descarga ZIP${sizeHint}...`, { duration: 4000 });

    try {
      const response = await fetch(`${API}/client/gallery/${file.file_id}/download-zip`, { headers: getAuthHeaders() });

      if (!response.ok) {
        throw new Error('Error al preparar el ZIP');
      }

      const data = await response.json();
      const parts = data.parts?.length ? data.parts : (data.download_url ? [{ part: 1, download_url: data.download_url, filename: data.filename }] : []);
      if (!parts.length) {
        throw new Error('Respuesta inválida del servidor');
      }

      if (parts.length === 1) {
        triggerDownload(parts[0].download_url, parts[0].filename || `${file.title}.zip`);
        toast.success('Descarga iniciada');
      } else {
        setZipParts({ title: file.title, image_count: data.image_count, parts, file_id: file.file_id, fetchedAt: Date.now() });
      }
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      toast.error('Error al descargar el ZIP');
    } finally {
      setDownloadingZip(null);
    }
  };

  const refreshZipParts = async () => {
    try {
      const response = await fetch(`${API}/client/gallery/${zipParts.file_id}/download-zip?refresh=true`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('refresh failed');
      const data = await response.json();
      return { ...zipParts, parts: data.parts, fetchedAt: Date.now() };
    } catch (error) {
      toast.error('No se pudo renovar el enlace de descarga');
      return null;
    }
  };

  const handleDownloadClick = (file) => {
    // If client hasn't left a testimonial, show the modal first
    if (!hasTestimonial) {
      setPendingDownload(file);
      setTestimonialModalOpen(true);
    } else {
      downloadZip(file);
    }
  };

  const handleTestimonialSubmit = () => {
    setHasTestimonial(true);
    setTestimonialModalOpen(false);
    // Save reference before clearing state, then trigger download with a delay
    // to ensure the modal has fully closed and React has re-rendered
    const fileToDownload = pendingDownload;
    setPendingDownload(null);
    if (fileToDownload) {
      setTimeout(() => downloadZip(fileToDownload), 300);
    }
  };

  const handleTestimonialSkip = () => {
    setTestimonialModalOpen(false);
    const fileToDownload = pendingDownload;
    setPendingDownload(null);
    if (fileToDownload) {
      setTimeout(() => downloadZip(fileToDownload), 300);
    }
  };

  const openNewSessionDataModal = () => {
    setEditingSessionData(null);
    setSessionDataModalOpen(true);
  };

  const openEditSessionDataModal = (sessionData) => {
    setEditingSessionData(sessionData);
    setSessionDataModalOpen(true);
  };

  const handleSessionDataModalClose = () => {
    setEditingSessionData(null);
    setSessionDataModalOpen(false);
  };

  const handleSessionDataSubmit = () => {
    fetchData(); // Refresh data
    setEditingSessionData(null);
    setSessionDataModalOpen(false);
  };

  const sessionTypeLabels = {
    recien_nacido: 'Recién Nacido',
    primera_comunion: 'Primera Comunión',
    boda: 'Boda',
    embarazo: 'Embarazo',
    familia: 'Familia'
  };

  const categoryLabels = {
    all: 'Todos',
    contratos: 'Contratos',
    tarifas: 'Tarifas',
    guias: 'Guías',
    otros: 'Otros'
  };

  const availableCategories = ['all', ...new Set(sharedDocs.map(doc => doc.category).filter(Boolean))];
  const filteredDocs = activeDocCategory === 'all' 
    ? sharedDocs 
    : sharedDocs.filter(doc => doc.category === activeDocCategory);

  if (!client && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <>
      <main className="pt-24 pb-20 min-h-screen bg-background" data-testid="private-area-page">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8"
          >
            <div>
              <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-2">
                Área Privada
              </span>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
                Hola, {client.name?.split(' ')[0]}
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                onClick={openNewSessionDataModal}
                className="gap-2"
                data-testid="session-data-btn"
              >
                <ClipboardList className="w-4 h-4" />
                <span className="hidden sm:inline">Informar datos de la sesión</span>
                <span className="sm:hidden">Datos sesión</span>
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium">{client.name}</p>
                  <p className="text-xs text-muted-foreground">{client.email}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Salir
              </Button>
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 flex-wrap">
            <button
              onClick={() => setActiveTab('private')}
              className={`flex items-center gap-2 px-4 py-3 transition-colors ${
                activeTab === 'private' 
                  ? 'bg-foreground text-background' 
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Lock className="w-4 h-4" />
              Mis Galerías
              {privateFiles.length > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'private' ? 'bg-background/20' : 'bg-foreground/10'}`}>
                  {privateFiles.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('shared')}
              className={`flex items-center gap-2 px-4 py-3 transition-colors ${
                activeTab === 'shared' 
                  ? 'bg-foreground text-background' 
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              Documentos
              {sharedDocs.length > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'shared' ? 'bg-background/20' : 'bg-foreground/10'}`}>
                  {sharedDocs.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('session')}
              className={`flex items-center gap-2 px-4 py-3 transition-colors ${
                activeTab === 'session' 
                  ? 'bg-foreground text-background' 
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
              data-testid="session-tab"
            >
              <ClipboardList className="w-4 h-4" />
              Mis Datos de Sesión
              {sessionDataList.length > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'session' ? 'bg-background/20' : 'bg-foreground/10'}`}>
                  {sessionDataList.length}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'private' ? (
              /* Private Files / Galleries */
              loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-48 bg-muted animate-pulse" />
                  ))}
                </div>
              ) : privateFiles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {privateFiles.map((file) => (
                    <div key={file.file_id} className="bg-card border border-border overflow-hidden group" data-testid={`file-${file.file_id}`}>
                      {/* Thumbnail - Clickable to open gallery */}
                      <div 
                        className="aspect-video bg-muted relative cursor-pointer"
                        onClick={() => file.file_type === 'gallery' && openGallery(file)}
                      >
                        {file.file_type === 'gallery' && (file.thumbnail || file.images?.[0]) ? (
                          <img 
                            src={file.thumbnail || file.images[0]} 
                            alt={file.title} 
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : file.file_type === 'document' ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <FileText className="w-16 h-16 text-muted-foreground" />
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Images className="w-16 h-16 text-muted-foreground" />
                          </div>
                        )}
                        
                        {/* Overlay on hover */}
                        {file.file_type === 'gallery' && file.images?.length > 0 && (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm uppercase tracking-wider">
                              Ver galería
                            </span>
                          </div>
                        )}
                        
                        {/* Photo count badge */}
                        {file.file_type === 'gallery' && file.images?.length > 0 && (
                          <span className="absolute top-3 right-3 bg-black/70 text-white text-sm px-3 py-1">
                            {file.images.length} fotos
                          </span>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="p-4">
                        <h3 className="font-serif text-xl mb-2">{file.title}</h3>
                        {file.description && (
                          <p className="text-sm text-muted-foreground mb-4">{file.description}</p>
                        )}
                        
                        {file.file_type === 'gallery' && file.images?.length > 0 ? (
                          <>
                            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2" data-testid={`gallery-info-${file.file_id}`}>
                              <Images className="w-3.5 h-3.5" />
                              <span>{file.images.length} foto{file.images.length === 1 ? '' : 's'}</span>
                              {file.total_size_bytes > 0 && (
                                <>
                                  <span className="opacity-50">·</span>
                                  <span>≈ {formatBytes(file.total_size_bytes)}</span>
                                </>
                              )}
                            </div>
                            <div className="flex gap-2">
                            <Button 
                              onClick={() => openGallery(file)}
                              variant="outline"
                              className="flex-1 gap-2"
                              data-testid={`view-gallery-btn-${file.file_id}`}
                            >
                              <Images className="w-4 h-4" />
                              Ver Galería
                            </Button>
                            <Button 
                              onClick={() => handleDownloadClick(file)}
                              disabled={downloadingZip === file.file_id}
                              className="flex-1 gap-2"
                              data-testid={`download-zip-btn-${file.file_id}`}
                            >
                              {downloadingZip === file.file_id ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-background border-t-transparent rounded-full animate-spin" />
                                  Preparando...
                                </>
                              ) : (
                                <>
                                  <Archive className="w-4 h-4" />
                                  Descargar ZIP
                                </>
                              )}
                            </Button>
                          </div>
                          </>
                        ) : file.file_url ? (
                          <a href={file.file_url} target="_blank" rel="noopener noreferrer">
                            <Button className="gap-2 w-full">
                              <Download className="w-4 h-4" />
                              Descargar
                            </Button>
                          </a>
                        ) : (
                          <Button disabled className="gap-2 w-full">
                            <Download className="w-4 h-4" />
                            Próximamente
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-muted/50 border border-border">
                  <Images className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-serif text-xl mb-2">Aún no tienes galerías</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Cuando contrates un servicio fotográfico, tus galerías de fotos aparecerán aquí 
                    para que puedas verlas y descargarlas.
                  </p>
                </div>
              )
            ) : activeTab === 'shared' ? (
              /* Shared Documents */
              loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-muted animate-pulse" />
                  ))}
                </div>
              ) : sharedDocs.length > 0 ? (
                <div className="space-y-6">
                  {/* Category Filter */}
                  {availableCategories.length > 2 && (
                    <div className="flex flex-wrap gap-2">
                      {availableCategories.map((category) => (
                        <button
                          key={category}
                          onClick={() => setActiveDocCategory(category)}
                          className={`px-4 py-2 text-sm transition-colors ${
                            activeDocCategory === category
                              ? 'bg-foreground text-background'
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                          data-testid={`doc-filter-${category}`}
                        >
                          {categoryLabels[category] || category}
                          {category !== 'all' && (
                            <span className="ml-2 opacity-60">
                              ({sharedDocs.filter(d => d.category === category).length})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Documents List */}
                  <div className="space-y-2">
                    {filteredDocs.map((doc) => {
                      const downloadUrl = doc.file_url.includes('cloudinary.com') 
                        ? doc.file_url.replace('/upload/', '/upload/fl_attachment/') 
                        : doc.file_url;
                      return (
                        <div 
                          key={doc.document_id}
                          className="flex items-center gap-4 p-4 bg-card border border-border group"
                        >
                          <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-red-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{doc.title}</p>
                            <div className="flex items-center gap-2">
                              {doc.description && (
                                <p className="text-sm text-muted-foreground truncate">{doc.description}</p>
                              )}
                              <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground">
                                {categoryLabels[doc.category] || doc.category}
                              </span>
                            </div>
                          </div>
                          <a 
                            href={downloadUrl}
                            download
                            className="flex items-center gap-2 px-4 py-2 bg-foreground text-background hover:bg-foreground/90 transition-colors text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Descargar
                          </a>
                        </div>
                      );
                    })}
                  </div>

                  {filteredDocs.length === 0 && activeDocCategory !== 'all' && (
                    <div className="text-center py-8 bg-muted/30 border border-border">
                      <p className="text-muted-foreground">
                        No hay documentos en esta categoría
                      </p>
                      <button 
                        onClick={() => setActiveDocCategory('all')}
                        className="text-sm text-foreground underline mt-2"
                      >
                        Ver todos los documentos
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16 bg-muted/50 border border-border">
                  <FolderOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-serif text-xl mb-2">Sin documentos disponibles</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Los contratos, tarifas y otros documentos importantes aparecerán aquí.
                  </p>
                </div>
              )
            ) : activeTab === 'session' ? (
              /* Session Data */
              loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-32 bg-muted animate-pulse" />
                  ))}
                </div>
              ) : sessionDataList.length > 0 ? (
                <div className="space-y-4">
                  {sessionDataList.map((session) => (
                    <div 
                      key={session.session_data_id} 
                      className="bg-card border border-border p-4"
                      data-testid={`session-${session.session_data_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-serif text-xl">
                            {sessionTypeLabels[session.session_type] || session.session_type}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {session.updated_at 
                              ? `Actualizado: ${new Date(session.updated_at).toLocaleDateString('es-ES')}`
                              : `Creado: ${new Date(session.created_at).toLocaleDateString('es-ES')}`
                            }
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditSessionDataModal(session)}
                          className="gap-2"
                          data-testid={`edit-session-${session.session_data_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                          Editar
                        </Button>
                      </div>
                      
                      {/* Session data summary */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        {Object.entries(session.data || {}).slice(0, 6).map(([key, value]) => (
                          <div key={key} className="bg-muted/50 p-2">
                            <span className="text-xs text-muted-foreground block">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className="font-medium truncate block">{value || '-'}</span>
                          </div>
                        ))}
                      </div>
                      
                      {Object.keys(session.data || {}).length > 6 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          +{Object.keys(session.data).length - 6} campos más
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-muted/50 border border-border">
                  <ClipboardList className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-serif text-xl mb-2">Sin datos de sesión</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-4">
                    Aquí podrás ver y editar los datos que hayas enviado sobre tus sesiones fotográficas.
                  </p>
                  <Button onClick={openNewSessionDataModal} className="gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Informar datos de sesión
                  </Button>
                </div>
              )
            ) : null}
          </motion.div>

          {/* Help Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-12 p-6 bg-muted/50 border border-border"
          >
            <h3 className="font-medium mb-2">¿Necesitas ayuda?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Si tienes alguna pregunta sobre tus archivos o necesitas soporte técnico, 
              no dudes en contactarnos.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = '/#contacto'}
            >
              Contactar
            </Button>
          </motion.div>
        </div>
      </main>

      <ZipPartsDialog data={zipParts} onClose={() => setZipParts(null)} onRefresh={refreshZipParts} />

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            {/* Close button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Title and counter */}
            <div className="absolute top-4 left-4 z-10 text-white">
              <h3 className="font-serif text-xl">{lightboxTitle}</h3>
              <p className="text-white/70 text-sm">{lightboxIndex + 1} / {lightboxImages.length}</p>
            </div>

            {/* Previous button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            {/* Image */}
            <motion.img
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={lightboxImages[lightboxIndex]}
              alt={`${lightboxTitle} - ${lightboxIndex + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            {/* Thumbnails */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2">
              {lightboxImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(idx);
                  }}
                  className={`w-16 h-12 flex-shrink-0 overflow-hidden transition-all ${
                    idx === lightboxIndex ? 'ring-2 ring-white' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Download current image */}
            <a
              href={lightboxImages[lightboxIndex]}
              download
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-20 right-4 z-10 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              Descargar imagen
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Testimonial Modal */}
      <TestimonialModal
        isOpen={testimonialModalOpen}
        onClose={() => {
          setTestimonialModalOpen(false);
          setPendingDownload(null);
        }}
        onSubmit={handleTestimonialSubmit}
        onSkip={handleTestimonialSkip}
      />

      {/* Session Data Modal */}
      <SessionDataModal
        isOpen={sessionDataModalOpen}
        onClose={handleSessionDataModalClose}
        onSubmit={handleSessionDataSubmit}
        existingData={editingSessionData}
      />
    </>
  );
};

export default PrivateArea;
